import moment from 'moment';
import { ELSDropDownOption } from '../../components/els.components';
import {
  LibrarySelectionDto,
  LibrarySortDto
} from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { SEGMENT_SIZE_ALL } from './library-page.constants';

export type LibraryChatFilter = {
  chatId: number;
  title: string;
};

export type LibraryEntryFilter = {
  entryId: number;
  text: string;
};

// The part of a library page's state that picks which items the table, View and (quiz) Take cover
export type LibrarySelectionState = {
  // Debounced search input, the value actually sent to NACS
  search: string;
  isbn: string;
  // Set from a row's options menu; shown as a removable filter pill
  chatFilter: LibraryChatFilter;
  // Set from a row's options menu; shown as a removable filter pill
  entryFilter: LibraryEntryFilter;
  createdFrom: Date;
  createdTo: Date;
  sort: LibrarySortDto;
  // SEGMENT_SIZE_ALL, or a number of items per segment
  segmentSize: string;
  // Zero-based index of the chosen segment of that size
  segment: string;
};

// Changing a filter changes how many items match, so the old page and segment no longer mean anything
export const LIBRARY_SELECTION_RESET = {
  page: 0,
  segmentSize: SEGMENT_SIZE_ALL,
  segment: '0',
};

export const isValidDate = (date: unknown): date is Date => date instanceof Date && !Number.isNaN(date.getTime());

// An empty box means no date filter, so only typed text that does not parse is invalid
export const isInvalidDateInput = (date: unknown, { originalInput }: { originalInput?: unknown } = {}): boolean =>
  !isValidDate(date) && typeof originalInput === 'string' && originalInput.trim() !== '';

// ELSDate only re-runs its validators when its value prop changes, and typed junk leaves the date as null.
// Passing this stable invalid Date while the text is invalid makes the message show and clear on time.
export const INVALID_DATE_VALUE = new Date(NaN);

// Typed text that is not a date leaves the filter off, so the box has to say so
export const getInvalidDateValidator = (isInvalid: boolean) => (): { id: string; content: string; isValid: boolean } => ({
  id: 'invalidDate',
  content: 'Enter a valid date',
  isValid: !isInvalid,
});

// "All <items>" first, then every size from 1 to the number of matching items
export const getSegmentSizeOptions = (totalMatches: number, itemsLabel: string): ELSDropDownOption[] => [
  { name: `All ${itemsLabel} (${totalMatches})`, value: SEGMENT_SIZE_ALL },
  ...Array.from({ length: totalMatches }, (_, index) => ({
    name: String(index + 1),
    value: String(index + 1),
  })),
];

// Consecutive windows of `segmentSize` items, e.g. 1-10, 11-20, ... The last one is shorter when
// the total is not a multiple of the size. A segment is a position in the sorted matches.
export const getSegmentOptions = (totalMatches: number, segmentSize: number): ELSDropDownOption[] => (
  Array.from({ length: Math.ceil(totalMatches / segmentSize) }, (_, index) => {
    const start = index * segmentSize + 1;
    const end = Math.min((index + 1) * segmentSize, totalMatches);
    return {
      name: start === end ? String(start) : `${start}-${end}`,
      value: String(index),
    };
  })
);

// The 1-based inclusive window NACS should take from the sorted matches; none for "All".
// The end is not clamped to the match count: NACS clamps it, so a stale count can't shorten the window.
export const getSegmentWindow = (
  segmentSize: string,
  segment: string
): Pick<LibrarySelectionDto, 'startIndex' | 'endIndex'> => {
  if (segmentSize === SEGMENT_SIZE_ALL) {
    return {};
  }
  const size = Number(segmentSize);
  const startIndex = Number(segment) * size + 1;
  return { startIndex, endIndex: startIndex + size - 1 };
};

// What the table, View and (quiz) Take all use, so they always cover the same items. NACS compares created_at
// with a plain inclusive range, so the student's local day bounds are sent as ISO-8601 UTC.
export const getLibrarySelection = (state: LibrarySelectionState): LibrarySelectionDto => ({
  search: state.search || undefined,
  isbn: state.isbn || undefined,
  chatId: state.chatFilter?.chatId,
  entryId: state.entryFilter?.entryId,
  // UTC ("Z"), not a local offset: a "+hh:mm" offset loses its "+" on the way to NACS and is rejected with a 422
  createdFrom: state.createdFrom ? moment(state.createdFrom).startOf('day').toISOString() : undefined,
  createdTo: state.createdTo ? moment(state.createdTo).endOf('day').toISOString() : undefined,
  sort: state.sort,
  ...getSegmentWindow(state.segmentSize, state.segment),
});

// Results are only shown while the selection still matches the request that loaded them, so after a
// filter, sort or segment change the previous rows never render or enable View/Take for the new selection.
// Page is left out so the current page stays on screen while the next one loads.
export const getLibraryResultsKey = (state: LibrarySelectionState): string => JSON.stringify(getLibrarySelection(state));
